import { CanDeactivateGuard } from './core/services/can-deactivate-guard.service';
import { NgModule, ErrorHandler, inject, provideAppInitializer, runInInjectionContext, Injector } from '@angular/core';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { MESSAGE_FORMAT_CONFIG } from 'ngx-translate-messageformat-compiler';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { BrowserModule } from '@angular/platform-browser';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ConfigurationModule } from './configuration/configuration.module';
import { HttpTokenInterceptor } from './auth/interceptors/http-token.interceptor';
import { AuthService } from './auth/services/auth.service';
import { UsersService } from './auth/services/users.service';
import { GlobalErrorHandler } from './core/services/global-error-handler';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { AuthGuard } from './auth/services/auth-guard.service';
import { PlatformAdminAuthGuard } from './auth/services/platform-admin-auth-guard.service';
import { OrganizationsAdminGuard } from './organizations-settings/services/organizations-admin-guard.service';
import { LanguageService } from './core/services/language.service';
import { CoreModule } from 'src/app/core/core.module';
import { TranslationsLoader } from './translations/classes/translationsloader';
import { ConfigurationService } from './configuration/services/configuration.service';
import { PlatformAdministrationService } from './platform-administration/services/platform-administration.service';
import { OrganizationsMemberGuard } from './organizations-settings/services/organizations-member-guard.service';
import { OrganizationsOnboardedGuard } from './organizations-settings/services/organizations-onboarded-guard.service';
import { OrganizationsConsultantGuard } from './organizations-settings/services/organizations-consultant-guard.service';
import { formatDate } from '@angular/common';
import { PlatformAdminOrServiceUserAuthGuard } from './auth/services/Platform-admin-serviceuser-auth-guard.service';
import { OrganizationsConsultantOrServiceUserGuard } from './organizations-settings/services/organizations-consultant-serviceuser-guard.service';
import { firstValueFrom } from 'rxjs';

export function appFactory(
  configService: ConfigurationService,
  translateService: TranslateService,
  injector: Injector
) {
  return async () => {
    await configService.load();
    
    translateService.setDefaultLang('en-US');
    await firstValueFrom(translateService.use('en-US'));
    
    const usersService = runInInjectionContext(injector, () => inject(UsersService));
    const platformAdminService = runInInjectionContext(injector, () => inject(PlatformAdministrationService));
    const authService = runInInjectionContext(injector, () => inject(AuthService));
    await authService.initialize();
  };
}

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        CoreModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: TranslationsLoader,
                deps: [HttpClient, ConfigurationService],
            },
            isolate: false,
        }),
        CoreModule.forRoot(),
        AppRoutingModule,
        ConfigurationModule], providers: [
        AuthService,
        AuthGuard,
        PlatformAdminAuthGuard,
        PlatformAdminOrServiceUserAuthGuard,
        OrganizationsAdminGuard,
        OrganizationsMemberGuard,
        OrganizationsOnboardedGuard,
        OrganizationsConsultantGuard,
        OrganizationsConsultantOrServiceUserGuard,
        CanDeactivateGuard,
        UsersService,
        PlatformAdministrationService,
        LanguageService,
        { provide: MESSAGE_FORMAT_CONFIG, useValue: { locales: ['en-US'] } },
        { provide: HTTP_INTERCEPTORS, useClass: HttpTokenInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
      provideAppInitializer(() => {
        const initializerFn = appFactory(
          inject(ConfigurationService),
          inject(TranslateService),
          inject(Injector)
        );
        return initializerFn();
      }),
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {
  constructor() {
    this.overrideDate();
  }

  overrideDate() {
    Date.prototype.toJSON = function (key: Date) {
      return formatDate(this, 'M/d/yyyy, hh:mm:ss a', 'en_US');
    };
  }
}
